import type { Workshop, WorkshopStatus } from "@/content/workshops";
import type { OpenModal } from "@/features/workshop/modals/useWorkshopModals";

/**
 * Phase data for the RunSequence panel. Ported from the Loveable v1
 * RunSequence reference (`src/content/workshops.ts → phases`), where
 * every workshop carries an ordered list of phases each tagged with a
 * `status`, a short `name`, and a bulleted `objectives` list.
 *
 * The legacy vanilla shipped per-workshop agenda summaries (see
 * `_legacy/ws4-session.js → WS4_SESSION.agenda` and the agenda blocks
 * in `_legacy/xaes-content.js`) but never structured them as a phase
 * stepper — that shape is a Loveable v1 invention we're carrying
 * forward. For workshops where no real phase data has landed yet, the
 * entries below use em-dash placeholders to mirror the conventions in
 * `_legacy/ws4-session.js`. WS4 is populated with a real port of the
 * Loveable v1 phases because that's the in-flight workshop the spec
 * calls out.
 */

export type PhaseStatus = "complete" | "active" | "upcoming" | "locked";

/** A phase objective. Either plain text, or a launchable row that opens
 *  a specific modal when clicked. WS4 Activity 2 uses the launchable
 *  shape to wire the Handshake + Stress-Test rows to their modals. */
export type PhaseObjective =
  | string
  | { text: string; launch: Exclude<OpenModal, null> };

export type PhaseDef = {
  /** Stable id used for the React key. */
  id: string;
  /** Short label in mono caps (e.g. "Intro", "Activity 1"). */
  title: string;
  status: PhaseStatus;
  /** Optional list of phase objectives shown when the phase expands. */
  objectives?: PhaseObjective[];
};

/**
 * Roll a workshop's effective phase statuses up into a single workshop
 * status. The workshop badge is DERIVED from its phases — never set
 * independently — so this is the one place the mapping lives and the
 * navigator can never advertise "done" for a workshop whose phases say
 * otherwise:
 *
 *   - every phase complete                      → "done"
 *   - some phase started (complete/active),
 *     but not all complete                      → "active"
 *   - nothing started (all upcoming/locked)     → "locked"
 *
 * An empty phase roster falls back to "locked" (nothing to run yet).
 * Callers pass the EFFECTIVE statuses (override ?? static default), so
 * the rollup reflects exactly what the stepper shows.
 */
export function deriveWorkshopStatus(phases: PhaseStatus[]): WorkshopStatus {
  if (phases.length === 0) return "locked";
  if (phases.every((s) => s === "complete")) return "done";
  if (phases.some((s) => s === "complete" || s === "active")) return "active";
  return "locked";
}

/**
 * Normalise a workshop's phase statuses from the single source of truth
 * — the set of completed phase ids. "active" (the blue current-phase
 * marker) is DERIVED, never toggled directly, so there is always exactly
 * one active phase: the FIRST phase that isn't complete. Everything
 * after it is "upcoming".
 *
 * This is what makes completion behave correctly in every direction:
 *   - complete a phase  → it drops out of the not-complete list, so the
 *     marker advances to the next incomplete phase.
 *   - uncomplete a phase → it re-enters the list; the marker falls back
 *     to the earliest incomplete phase (often the one just reopened).
 *   - uncomplete a MIDDLE phase while later phases stay complete → the
 *     reopened phase becomes the (earliest) active one and the later
 *     completes are left untouched — a legitimate "gap" state.
 *
 * When every phase is complete there is no active phase (the workshop is
 * done); when none are, the first phase is the active starting point.
 * `orderedIds` must be in stepper order — that order defines "first".
 */
export function normalizePhaseStatuses(
  orderedIds: string[],
  completeIds: Set<string>,
): Record<string, PhaseStatus> {
  const out: Record<string, PhaseStatus> = {};
  let activeAssigned = false;
  for (const id of orderedIds) {
    if (completeIds.has(id)) {
      out[id] = "complete";
    } else if (!activeAssigned) {
      out[id] = "active";
      activeAssigned = true;
    } else {
      out[id] = "upcoming";
    }
  }
  return out;
}

/**
 * RT-AGENDA — the two reusable activity surfaces. RT-CANVAS generalised the
 * Handshake + Stress-Test *boards* to every workshop (each renders its own
 * authored content, or its empty state where unauthored); these are the
 * launchable rows that open them. WS4 declares its own authored variants;
 * the post-construction step below appends these generic rows to every other
 * workshop's hands-on "Activity 2" phase so the activity is reachable
 * everywhere rather than gated to WS4.
 */
const ACTIVITY_LAUNCH_ROWS: PhaseObjective[] = [
  { text: "Run the Human–Agent Handshake activity", launch: "handshake" },
  { text: "Run the Stress-Test role-play (FUD × Adoption)", launch: "stress-test" },
];

export const PHASES_BY_WORKSHOP: Record<Workshop["id"], PhaseDef[]> = {
  // WS1 — Executive Alignment (kick-off: purpose, vision, outcomes,
  // commitment). Mirrors the legacy `_legacy/mc.html` WS1 bubble set
  // (PURPOSE / VISION / OUTCOMES / COMMITMENT) at lines 478-540.
  WS1: [
    {
      id: "ws1-intro",
      title: "Intro",
      status: "complete",
      objectives: [
        "Frame the XAES mission and the executive charter",
        "Confirm sponsors, stakeholders, and decision rights",
      ],
    },
    {
      id: "ws1-a1",
      title: "Activity 1",
      status: "complete",
      objectives: [
        "Articulate purpose — why we are doing this",
        "Sketch the vision for the agentic enterprise",
      ],
    },
    {
      id: "ws1-a2",
      title: "Activity 2",
      status: "complete",
      objectives: [
        "Agree the outcomes that define success",
        "Capture the commitment & non-negotiables",
      ],
    },
    {
      id: "ws1-outcome",
      title: "Outcome",
      status: "complete",
      objectives: [
        "Publish the executive alignment statement",
        "Hand over to the Current-Process workshop",
      ],
    },
  ],
  // WS2 — Current Process Selection & Baseline. Mirrors the legacy
  // WS2 panel (CURRENT STATE MAP / PAIN POINTS / OPPORTUNITY INSIGHTS
  // / PROCESS SELECTION) at `_legacy/mc.html` lines 624-682.
  WS2: [
    {
      id: "ws2-intro",
      title: "Intro",
      status: "complete",
      objectives: [
        "Recap the executive alignment & charter",
        "Set scope for the as-is baseline",
      ],
    },
    {
      id: "ws2-a1",
      title: "Activity 1",
      status: "complete",
      objectives: [
        "Map the current end-to-end process",
        "Surface pain points & friction sources",
      ],
    },
    {
      id: "ws2-a2",
      title: "Activity 2",
      status: "complete",
      objectives: [
        "Identify opportunity insights & quick wins",
        "Select the priority process for the future state",
      ],
    },
    {
      id: "ws2-outcome",
      title: "Outcome",
      status: "complete",
      objectives: [
        "Lock the baseline metrics & sources",
        "Brief the To-Be design workshop",
      ],
    },
  ],
  // WS3 — To Be AI Enhanced Process Design. Mirrors WS3 bubbles
  // (HIGHER CONVERSION / LOWER COST / FASTER CYCLE TIME / SAFE &
  // COMPLIANT / BETTER EXPERIENCE) at `_legacy/mc.html` lines 768-830.
  WS3: [
    {
      id: "ws3-intro",
      title: "Intro",
      status: "complete",
      objectives: [
        "Replay the as-is pain points",
        "Frame the to-be design principles",
      ],
    },
    {
      id: "ws3-a1",
      title: "Activity 1",
      status: "complete",
      objectives: [
        "Sketch the AI-enhanced target flow",
        "Identify the highest-leverage automation points",
      ],
    },
    {
      id: "ws3-a2",
      title: "Activity 2",
      status: "complete",
      objectives: [
        "Trade conversion vs. cost vs. cycle time",
        "Pressure-test against compliance guardrails",
      ],
    },
    {
      id: "ws3-outcome",
      title: "Outcome",
      status: "complete",
      objectives: [
        "Publish the To-Be process blueprint",
        "Hand over to Human-Agent Ways of Working",
      ],
    },
  ],
  // WS4 — Human-Agent Ways of Working. Real port from Loveable v1
  // (the in-flight workshop). Activity 2 stays `active` so the
  // stepper highlights the row that launches the Handshake +
  // Stress-Test modals out of the box.
  WS4: [
    {
      id: "ws4-intro",
      title: "Intro",
      status: "complete",
      objectives: [
        "Set context and objectives",
        "Introduce workshop structure",
      ],
    },
    {
      id: "ws4-a1",
      title: "Activity 1",
      status: "complete",
      objectives: [
        "Define human roles",
        "Map current ways of working",
      ],
    },
    {
      id: "ws4-a2",
      title: "Activity 2",
      status: "active",
      objectives: [
        { text: "Define the Human–Agent Handshake", launch: "handshake" },
        { text: "Stress-Test Role Play (FUD x Adoption)", launch: "stress-test" },
      ],
    },
    {
      id: "ws4-a3",
      title: "Activity 3",
      status: "upcoming",
      objectives: [
        "Define operating model",
        "Assign responsibilities",
      ],
    },
    {
      id: "ws4-outcome",
      title: "Outcome",
      status: "upcoming",
      objectives: [
        "Consolidate outputs",
        "Agree next steps",
      ],
    },
  ],
  // WS5 — Governance, Risk Compliance & Safety. Mirrors the WS5
  // bubble set (AUDIT & TRACEABILITY / MONITORING & DETECTION /
  // CONTROLS & GUARDRAILS / POLICIES & STANDARDS / APPROVALS) at
  // `_legacy/mc.html` lines 900-970. All phases sit at `upcoming`
  // because every workshop after WS4 has not yet been delivered.
  WS5: [
    {
      id: "ws5-intro",
      title: "Intro",
      status: "upcoming",
      objectives: [
        "Recap the To-Be process design",
        "Frame the risk lens for agentic operations",
      ],
    },
    {
      id: "ws5-a1",
      title: "Activity 1",
      status: "upcoming",
      objectives: [
        "Map controls, guardrails, and audit hooks",
        "Define monitoring & detection signals",
      ],
    },
    {
      id: "ws5-a2",
      title: "Activity 2",
      status: "upcoming",
      objectives: [
        "Set policies, standards, and approval gates",
        "Decide where human oversight is required",
      ],
    },
    {
      id: "ws5-outcome",
      title: "Outcome",
      status: "upcoming",
      objectives: [
        "Publish the governance & safety blueprint",
        "Hand over to the Target Operating Model",
      ],
    },
  ],
  // WS6 — Phase 0 Blueprint Demo. Mirrors the WS6 bubble set
  // (EXPERIENCE / GOVERNANCE / DATA & KNOWLEDGE / TECHNOLOGY /
  // PROCESS) at `_legacy/mc.html` lines 1356-1410. All phases sit
  // at `upcoming` (future workshop, nothing delivered yet).
  WS6: [
    {
      id: "ws6-intro",
      title: "Intro",
      status: "upcoming",
      objectives: [
        "Stitch WS1-WS5 outputs into the blueprint",
        "Confirm the operating-model lenses",
      ],
    },
    {
      id: "ws6-a1",
      title: "Activity 1",
      status: "upcoming",
      objectives: [
        "Walk the experience, process, and data lenses",
        "Validate technology platforms & integrations",
      ],
    },
    {
      id: "ws6-a2",
      title: "Activity 2",
      status: "upcoming",
      objectives: [
        "Sketch the governance & adoption mechanisms",
        "Demo the end-to-end target operating model",
      ],
    },
    {
      id: "ws6-outcome",
      title: "Outcome",
      status: "upcoming",
      objectives: [
        "Lock the Phase 0 blueprint for executive review",
        "Brief the Executive Showcase workshop",
      ],
    },
  ],
  // WS7 — Executive Showcase. Mirrors the WS7 bubble set
  // (DEMONSTRATE VALUE / VALIDATE OUTCOMES / BUILD CONFIDENCE /
  // ALIGN ON NEXT STEPS) at `_legacy/mc.html` lines 1054-1097. All
  // phases sit at `upcoming` (future workshop, nothing delivered).
  WS7: [
    {
      id: "ws7-intro",
      title: "Intro",
      status: "upcoming",
      objectives: [
        "Set the showcase narrative",
        "Confirm the executive audience & timing",
      ],
    },
    {
      id: "ws7-a1",
      title: "Activity 1",
      status: "upcoming",
      objectives: [
        "Demonstrate the blueprint against real scenarios",
        "Validate outcomes vs. the executive charter",
      ],
    },
    {
      id: "ws7-a2",
      title: "Activity 2",
      status: "upcoming",
      objectives: [
        "Build confidence with evidence & trust signals",
        "Align on the path forward & next decisions",
      ],
    },
    {
      id: "ws7-outcome",
      title: "Outcome",
      status: "upcoming",
      objectives: [
        "Capture executive commitments & sign-off",
        "Hand over to the PREPARE flight plan",
      ],
    },
  ],
  // WS8 / PREPARE — Flight Plan. Mirrors the WS8 bubble set (PHASE 0
  // / PHASE 1 / PHASE 2+ / BEYOND) at `_legacy/mc.html` lines
  // 1186-1290. All phases sit at `upcoming` (future workshop).
  WS8: [
    {
      id: "ws8-intro",
      title: "Intro",
      status: "upcoming",
      objectives: [
        "Translate executive sign-off into a delivery plan",
        "Sequence Phase 0 → Phase 1 → Phase 2+",
      ],
    },
    {
      id: "ws8-a1",
      title: "Activity 1",
      status: "upcoming",
      objectives: [
        "Lock Phase 0 scope, owners, and milestones",
        "Wire up the implementation crew & rituals",
      ],
    },
    {
      id: "ws8-a2",
      title: "Activity 2",
      status: "upcoming",
      objectives: [
        "Define adoption & scale-out triggers",
        "Set sustain, innovate & evolve checkpoints",
      ],
    },
    {
      id: "ws8-outcome",
      title: "Outcome",
      status: "upcoming",
      objectives: [
        "Publish the XAES flight plan",
        "Kick off implementation",
      ],
    },
  ],
};

// RT-AGENDA — un-gate the WS4-only activity prompt cards: append the two
// reusable launch rows (Handshake + Stress-Test) to every *other* workshop's
// "Activity 2" phase so each surfaces the activity boards. WS4 keeps its own
// authored rows. Runs once at module load, before any consumer reads the map;
// each phase gets a fresh objectives array (the row objects are read-only and
// safely shared). The board shows the workshop's own content, or its empty
// state where unauthored.
for (const [wsId, phases] of Object.entries(PHASES_BY_WORKSHOP)) {
  if (wsId === "WS4") continue;
  const activity = phases.find((p) => p.id === `${wsId.toLowerCase()}-a2`);
  if (activity) {
    activity.objectives = [...(activity.objectives ?? []), ...ACTIVITY_LAUNCH_ROWS];
  }
}

/* ----------------------- P0.4 content getters ---------------------- */
/**
 * Public content getters frozen by the Phase-0 contract. Track A imports
 * these read-only (Analyst grounding) while Track B owns the underlying
 * data; both tracks share one stable signature. These project the
 * internal {@link PhaseDef} (which carries UI-only `status`/`launch`
 * fields) down to the plain content shape downstream consumers need.
 */

/** A workshop phase as a content summary — no UI status / launch wiring. */
export type WorkshopPhaseSummary = {
  /** Short phase label (e.g. "Intro", "Activity 1"). */
  name: string;
  /** Plain-text objectives; launchable rows contribute their `text`. */
  objectives: string[];
};

/** Plain-text objectives for one phase (drops the launch wiring). */
function phaseObjectiveTexts(phase: PhaseDef): string[] {
  return (phase.objectives ?? []).map((o) =>
    typeof o === "string" ? o : o.text,
  );
}

/**
 * Phases for a workshop as content summaries, in stepper order.
 * `[]` for unknown ids.
 */
export function getWorkshopPhases(workshopId: string): WorkshopPhaseSummary[] {
  const phases = PHASES_BY_WORKSHOP[workshopId as Workshop["id"]] ?? [];
  return phases.map((p) => ({ name: p.title, objectives: phaseObjectiveTexts(p) }));
}

/**
 * Flat agenda bullets for a workshop, derived from its phases: each phase
 * contributes its name followed by its objectives. Used for the Analyst's
 * `<retrieved>`/agenda grounding and any lightweight agenda summary.
 * `[]` for unknown ids.
 */
export function getWorkshopAgenda(workshopId: string): string[] {
  const out: string[] = [];
  for (const phase of getWorkshopPhases(workshopId)) {
    out.push(phase.name);
    out.push(...phase.objectives);
  }
  return out;
}
